import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { ReactComponent as IconLeftArrow } from "../../styles/assets/svg/icon_LeftChevron.svg";
import { ReactComponent as IconRightArrow } from "../../styles/assets/svg/icon_RightChevron.svg";
import fire from "src/styles/assets/img/icon_fire.png";
import location from "src/styles/assets/img/icon_location.png";
import walkperson from "src/styles/assets/img/walkperson.png";
import WeeklyBarChart from "./components/WeeklyBarChart";
import { PageContainer } from "src/components/organisms/contents";
import { ModalDatePicker } from "src/components/molecules/ModalDatePicker";
import { ModalWalkDevice } from "src/components/molecules/ModalWalkDevice";
import { TopBackground } from "src/components/atoms/TopBackground/TopBackground";
import { ConHeader } from "src/components/atoms/MainHeaderBox/MainConHeader";
import { useQuery } from "@tanstack/react-query";
import { request } from "src/common/api";
import { BaseResponse } from "src/common/types/baseRespones";
import { GET_WALK } from "src/common/querykeys";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const titleText: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "24px",
  color: "var(--neutral-700, #404040)",
};

const captionText: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "16px",
  color: "var(--neutral-400, #A3A3A3)",
};

const valueText: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 800,
  lineHeight: "36px",
  color: "#111827",
};

export const WalkPage: React.FC = () => {
  const navi = useNavigate();

  const today = dayjs().format("YYYY.MM.DD");

  const [selectedDate, setSelectedDate] = useState<string>(today);
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);

  const [goal] = useState<number>(15000);
  const [weeklySteps, setWeeklySteps] = useState<number[]>([]); // 여기 값 바꿔주면 됨

  const avgSteps = useMemo(
    () =>
      weeklySteps.length
        ? Math.round(
            weeklySteps.reduce((a, b) => a + b, 0) / weeklySteps.length
          )
        : 0,
    [weeklySteps]
  );

  // 전체 걷기 데이터 불러오기
  const useAllWalkRegister = () => {
    return useQuery({
      queryKey: [GET_WALK],
      queryFn: async () => {
        const result = await request<BaseResponse<any>>({
          method: "GET",
          url: `/health-steps`,
        });
        return result.response || [];
      },
    });
  };

  // 해당 날짜 걷기 데이터 불러오기
  const useWalkRegister = (date?: string) => {
    return useQuery({
      queryKey: [GET_WALK, date],
      queryFn: async () => {
        const result = await request<BaseResponse<any>>({
          method: "GET",
          url: `/health-steps?date=${date}`,
        });
        return result.response || [];
      },
    });
  };

  const { data: allWalkData = [] } = useAllWalkRegister();
  const { data: walkData = [] } = useWalkRegister(selectedDate);

  useEffect(() => {
    if (!allWalkData || allWalkData.length === 0) {
      setWeeklySteps(Array(7).fill(0));
      return;
    }

    // selectedDate 기준 이번 주 월 ~ 일 계산
    const currentDate = dayjs(selectedDate.replace(/\./g, "-"), "YYYY.MM.DD");
    const startOfWeek = currentDate.startOf("week");
    const endOfWeek = startOfWeek.add(6, "day");

    // 이번 주 데이터만 필터링
    const weekData = allWalkData.filter((d: any) => {
      const dataDate = dayjs(d.date.replace(/\./g, "-"));
      return (
        dataDate.isSameOrAfter(startOfWeek, "day") &&
        dataDate.isSameOrBefore(endOfWeek, "day")
      );
    });

    if (weekData.length === 0) {
      // 이번 주 데이터가 없으면 0으로 초기화
      setWeeklySteps(Array(7).fill(0));
      return;
    }

    const weekSteps: number[] = Array(7).fill(0);
    weekData.forEach((d: any) => {
      const dataDate = dayjs(d.date);
      const diff = dataDate.diff(startOfWeek, "day");

      // diff 가 0(일요일)이라면 마지막 인덱스 6으로 이동
      const dayIndex = diff === 0 ? 6 : diff - 1;

      weekSteps[dayIndex] = Number(d.stepCount);
    });

    setWeeklySteps(weekSteps);
  }, [allWalkData, selectedDate]);

  const handlePrev = () => {
    setSelectedDate((prev) =>
      dayjs(prev.replace(/\./g, "-")).subtract(1, "day").format("YYYY.MM.DD")
    );
  };

  const handleNext = () => {
    setSelectedDate((prev) =>
      dayjs(prev.replace(/\./g, "-")).add(1, "day").format("YYYY.MM.DD")
    );
  };

  const formatDate = (y: number, m: number, d: number) =>
    `${y}.${String(m).padStart(2, "0")}.${String(d).padStart(2, "0")}`;

  const progress = Math.max(
    0,
    Math.min(1, (walkData[0]?.stepCount ?? 0) / (goal || 1))
  );

  return (
    <PageContainer isNavBar={false} active={null}>
      <>
        {/* 상단 배경 */}
        <TopBackground />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            position: "relative",
            zIndex: 1,
            paddingBottom: "70px",
          }}
        >
          {/* 헤더 */}
          <ConHeader
            title="걷기"
            onClickArrow={() => navi(-1)}
            onClickSetting={() => setModalShow(true)}
            setIcon={false}
          />

          {/* 날짜 선택 */}
          <div
            style={{
              position: "relative",
              width: "100%",
              height: 48,
              zIndex: 1,
              padding: 8,
              background: "var(--white, white)",
              boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.04)",
              borderRadius: 999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 8px",
              }}
            >
              <IconLeftArrow
                style={{ cursor: "pointer" }}
                onClick={handlePrev}
              />
              <div
                style={{ ...titleText, fontWeight: 700, cursor: "pointer" }}
                onClick={() => setOpenCalendar(true)}
              >
                {selectedDate}
              </div>
              <IconRightArrow
                style={{ cursor: "pointer" }}
                onClick={handleNext}
              />
            </div>
            {/* 모달 */}
            {openCalendar && (
              <ModalDatePicker
                data={selectedDate}
                title="날짜"
                onClose={() => setOpenCalendar(false)}
                onConfirm={(year, month, day) => {
                  setSelectedDate(formatDate(year, month, day));
                  setOpenCalendar(false);
                }}
              />
            )}
          </div>

          {/* 오늘의 걸음 카드 (좌: 제목+수치+바, 우: 이미지) */}
          <div
            style={{
              width: "100%",
              maxWidth: "45rem",
              height: 144,
              background: "#fff",
              borderRadius: 24,
              boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
              padding: "12px 16px 16px",
              boxSizing: "border-box",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 120px",
                alignItems: "center",
                columnGap: 12,
              }}
            >
              <div>
                <div style={{ ...titleText, marginBottom: 4 }}>오늘의 걸음</div>
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 6 }}
                >
                  <div style={valueText}>
                    {Number(walkData[0]?.stepCount ?? 0).toLocaleString()}
                  </div>
                  <div style={{ ...captionText, color: "#9CA3AF" }}>
                    / {goal.toLocaleString()}
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      height: 10,
                      borderRadius: 999,
                      background: "#E5E7EB",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${progress * 100}%`,
                        background: "linear-gradient(90deg,#FFA14A,#FF7F32)",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* 오른쪽: 캐릭터 이미지 */}
              <div style={{ justifySelf: "end" }}>
                <img
                  src={walkperson}
                  alt=""
                  width={112}
                  height={119}
                  style={{ display: "block" }}
                />
              </div>
            </div>
          </div>

          {/* 소모 칼로리 / 이동 거리 카드 */}
          <div
            style={{
              width: "100%",
              maxWidth: "45rem",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            <StatCard
              title="소모 칼로리"
              value={
                walkData?.[0] && walkData[0]?.calories !== null
                  ? `${Number(walkData[0]?.calories).toLocaleString()} Kcal`
                  : undefined
              }
              imgSrc={fire}
              imgAlt=""
            />
            <StatCard
              title="이동 거리"
              value={
                walkData?.[0] && walkData[0]?.distance !== null
                  ? `${Number(walkData[0]?.distance).toLocaleString()} km`
                  : undefined
              }
              imgSrc={location}
              imgAlt=""
            />
          </div>

          {/* 지난 7일 섹션 */}
          <div
            style={{
              width: "100%",
              maxWidth: "45rem",
              margin: "0 auto",
              background: "#fff",
              padding: 16,
              borderRadius: 24,
              boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
              boxSizing: "border-box",
            }}
          >
            {weeklySteps.length === 0 || weeklySteps.every((n) => n === 0) ? (
              <>
                <div style={{ ...titleText, marginBottom: 8 }}>
                  지난 7일간 기록이 없어요
                </div>
                <div
                  style={{
                    width: "100%",
                    maxWidth: "45rem",
                    borderRadius: 16,
                    overflow: "hidden",
                  }}
                >
                  <WeeklyBarChart
                    data={weeklySteps}
                    maxHint={15000}
                    height={255}
                    barWidth={24}
                    gap={14}
                  />
                </div>
              </>
            ) : (
              <>
                {/* 타이틀 */}
                <div style={{ width: "100%", maxWidth: "45rem" }}>
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "45rem",
                      height: 60,
                      display: "grid",
                      rowGap: 4,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "45rem",
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#404040",
                      }}
                    >
                      지난 7일간
                    </div>
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "45rem",
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      평균{" "}
                      <span style={{ color: "#F8932D", fontSize: 22 }}>
                        {avgSteps.toLocaleString()}
                      </span>{" "}
                      보 걸었어요!
                    </div>
                  </div>

                  <WeeklyBarChart
                    data={weeklySteps}
                    maxHint={15000}
                    height={255}
                    barWidth={24}
                    gap={14}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {modalShow && <ModalWalkDevice onClose={() => setModalShow(false)} />}
      </>
    </PageContainer>
  );
};

/** 작은 지표 카드: 아이콘을 우측 고정(세로 중앙 약간 아래) */
type StatCardProps = {
  title: string;
  value?: string;
  imgSrc: string;
  imgAlt: string;
  rowStart?: number;
  colStart?: number;
  rowSpan?: number;
  colSpan?: number;
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  imgSrc,
  imgAlt,
  rowStart,
  colStart,
  rowSpan = 1,
  colSpan = 1,
}) => (
  <div
    style={{
      width: "100%",
      height: 94,
      opacity: 1,
      borderRadius: 16,
      padding: 12,
      gap: 10,
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "#ECECEC",
      gridRowStart: rowStart,
      gridColumnStart: colStart,
      gridRowEnd: `span ${rowSpan}`,
      gridColumnEnd: `span ${colSpan}`,
      background: "#fff",
      boxSizing: "border-box",
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "start",
    }}
  >
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#262626",
          lineHeight: "18px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: "#737373",
          marginTop: 6,
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "18px",
        }}
      >
        {value ?? "아직 기록이 없어요."}
      </div>
    </div>

    {/* 아이콘 */}
    <div
      style={{
        placeSelf: "end end",
        width: 36,
        height: 36,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={imgSrc}
        alt={imgAlt}
        width={36}
        height={36}
        style={{ display: "block" }}
      />
    </div>
  </div>
);
// const EmptyGrid: React.FC = () => (
//   <div
//     style={{
//       height: 220,
//       borderRadius: 16,
//       background:
//         "linear-gradient(180deg, rgba(0,0,0,0.04) 1px, transparent 1px) 0 0/100% 44px,  linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px) 0 0/52px 100%",
//       marginTop: 8,
//     }}
//   />
// );

export default WalkPage;
